import { Component, h, State, Event, EventEmitter, Prop, Listen, Watch } from '@stencil/core';
import { DropdownItem } from '../dropdown-list/dropdown-list';
import { transformInstanceListToHierarchy } from '../../../services/transforms';
import { Env } from '@stencil/core';
import { SVGIcon } from '../icons/svg-icon';
import { ALL_PAGES_SIZE, APP_DIRECTORY_PRODUCT_ID, DASHBOARD_PRODUCT_ID } from '../../../constants';
import { emitSegmentTrackEvent } from '../../../services/segment';
import { ComponentEventBus } from '../../../services';
import { APP_DIRECTORY_OPTION, DASHBOARD_OPTION, IS_APP_DIRECTORY, IS_DASHBOARD } from '../../../utils';

@Component({
  tag: 'opti-product-switcher',
  styleUrl: 'opti-product-switcher.scss',
  shadow: true,
})
export class OptiProductSwitcher {
  private eventBusSubscription?: any;

  @Prop()
  selectionStorageKey: string;

  @Prop()
  disableNavigation?: boolean;

  @Prop({ attribute: 'organization-id' }) initialOrganizationId?: string;
  @Prop({ attribute: 'customer-master-id' }) orgCrmId?: string;

  // Manual loading prop
  @Prop({ attribute: 'show-loading' }) showLoading?: boolean;

  @Prop({ mutable: true })
  staticData: {
    organizations: DropdownItem[];
    products: DropdownItem[];
  };

  @Prop({ attribute: 'auth-token' }) authToken?: string;
  @Prop({ attribute: 'user' }) user?: { email: string; name: string; preferred_username?: string };
  @Watch('user')
  updateThisUser(val: { email: string; name: string; preferred_username?: string }) {
    // whenever the user changes and the data hasn't been fetched, trigger a fetch.
    // this is needed because the call looks for the user email so can't just rely on the auth-token passed in
    if (val && !this.data.organizations.length) {
      this.getUserInstancePermissions();
    }
  }
  @Prop() instanceId?: string;
  @Prop() orgName?: string;
  @Prop() productName?: string;

  @State() loading: boolean = true;
  @State() selectedOrganization: DropdownItem | null;
  @State() selectedProductInstance: DropdownItem | null;
  @State() data: {
    organizations: DropdownItem[];
    products: DropdownItem[];
  } = {
    organizations: [],
    products: [],
  };
  @State() enableAppDirectory: boolean = false;

  fetchingPermissions: boolean = false;
  homeOrganizationId: string | null;

  selectedOrg: DropdownItem[] | null;
  selectedProduct: DropdownItem[] | null;

  @Event({
    eventName: '@opti-product-switcher:change',
    composed: true,
    cancelable: true,
    bubbles: true,
  })
  select: EventEmitter<DropdownItem[]>;

  @Listen('@opti-product-switcher:set', { target: 'window', capture: true })
  handleManualSet(event: CustomEvent<any>) {
    const item: any = event.detail;
    const { value, type } = item;
    const { organizations, products = [] } = this.data || {};
    if (value === APP_DIRECTORY_PRODUCT_ID) {
      this.selectedProductInstance = APP_DIRECTORY_OPTION;
      this.select.emit([this.selectedOrganization, APP_DIRECTORY_OPTION]);
      return;
    } else if (type === 'instance') {
      const instanceToSet = products.find(p => !!p.subItems.find(instance => instance.value === value));
      if (instanceToSet) {
        const instanceOrg = organizations.find(o => o.value === instanceToSet.valueObject?.OrganizationId);
        this.selectedOrganization = instanceOrg;
        this.selectedProductInstance = instanceToSet;
        this.select.emit([this.selectedOrganization, this.selectedProductInstance]);
      }
    } else if (item.type === 'organization') {
      /*
      When emitting the organization, we should support an instance as well
      and fallback to the home app if one is not provided.
       */
      const { organizationId, instanceId = DASHBOARD_OPTION.value, productId } = value;
      const matchingOrg = organizations.find(o => o.value === organizationId);
      // if the value provided doesn't match an organization let's not do anything
      if (!matchingOrg) {
        return;
      }
      // find the corresponding instance or product
      const matchingProduct = products.find(p => p.value === productId);
      const matchingInstance = products.find(p => !!p.subItems.find(instance => instance.value === instanceId));

      /**
       * If there was no instance, but we were given a productId,
       * check if the product has more than one subitem (instances) and select the first option
       * otherwise, we'll just set the instance to be the product
       */
      if (matchingInstance) {
        this.selectedProductInstance = matchingInstance;
      } else if (!matchingInstance && matchingProduct && matchingProduct.subItems?.length > 1) {
        this.selectedProductInstance = matchingProduct.subItems[0];
      } else if (matchingProduct) {
        this.selectedProductInstance = matchingProduct;
      }

      this.selectedOrganization = matchingOrg;
      this.select.emit([this.selectedOrganization, this.selectedProductInstance]);
    }
  }

  @Listen('@opti-product-switcher:refetch', { target: 'window', capture: true })
  refetchUserInstancePermissions() {
    this.getUserInstancePermissions();
  }

  @Watch('staticData')
  async handleStaticData(data: any) {
    this.data = data;
    if (this.data.organizations.length) {
      this.goToItem(this.data.organizations[0], false);
    }
  }

  @Watch('instanceId')
  handleInstanceUpdate() {
    // if instanceId is updated, then reevaluate based on existing instancePermissions
    this.getUserInstancePermissions();
  }

  @Watch('authToken')
  handleAuthTokenUpdate() {
    this.loading = true;
    this.getUserInstancePermissions();
  }

  componentWillRender() {
    // 2nd argument is replay=true to replay last event since we have a race condition
    // for when the Optimizely client is loaded compared to when this component loads
    this.eventBusSubscription =
      !this.eventBusSubscription &&
      ComponentEventBus.subscribe<boolean>(`enableAppDirectory`, true, ({ payload }: { payload: boolean }) => {
        this.enableAppDirectory = payload;
      });

    // Don't make the call if static data is provided
    if (this.staticData) {
      this.loading = false;
      return;
    }
  }

  async componentDidRender() {
    if (this.loading && this.authToken) {
      await this.getUserInstancePermissions();
    } else {
      this.loading = false;
    }
  }

  disconnectedCallback() {
    this.eventBusSubscription.unsubscribe();
  }

  async getUserInstancePermissions() {
    const { email = '' } = this.user || {};
    if (!email) {
      this.loading = false;
      return;
    }

    if (!this.fetchingPermissions) {
      try {
        this.fetchingPermissions = true;
        const response = await fetch(`${Env.IDENTITY_SERVICE_URL}/api/users/${email.toLowerCase()}/instancepermissions?size=${ALL_PAGES_SIZE}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${this.authToken}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(response.statusText);
        }
        this.data = transformInstanceListToHierarchy(await response.json());
        this.homeOrganizationId = await this._getHomeOrganization(email, this.authToken);

        let selectedItem: DropdownItem;
        const matchingInstancePropItem: DropdownItem = this.data.products.flatMap(product => product.subItems).find(item => item.value === this.instanceId);
        const matchingUrlItem: DropdownItem = this.data.products.flatMap(product => product.subItems).find(item => window.location.href.includes(item.href));
        const isHomeApp: boolean = this.instanceId === DASHBOARD_PRODUCT_ID;
        const isAppDirectory: boolean = this.instanceId === APP_DIRECTORY_PRODUCT_ID;

        // * if state is already set, use that
        //    (for re-render without navigation - triggered by '@opti-product-switcher:set' event)
        // * else if instance-id is provided, use it if found in list
        //    (Home is not in instancePermissions)
        // * else if we have a matching url, set first matching instances
        // * else set org to homeOrg or first org
        //    and check if Home app
        if (this.selectedProductInstance) {
          selectedItem = this.selectedProductInstance;
        } else if (matchingInstancePropItem) {
          selectedItem = matchingInstancePropItem;
        } else if (matchingUrlItem) {
          selectedItem = matchingUrlItem;
        } else {
          // look for the organization from the prop if provided, fallback to home org by default or if not found
          const homeOrg = this.data.organizations.find(org => org.value === this.homeOrganizationId);
          const orgFromProp = this.initialOrganizationId ? this.data.organizations.find(org => org.value === this.initialOrganizationId) : null;
          const initialOrg = orgFromProp || homeOrg;

          if (initialOrg) {
            selectedItem = initialOrg;
          } else {
            selectedItem = this.data.organizations[0];
          }
          if (isHomeApp) {
            this.selectedProductInstance = DASHBOARD_OPTION;
          } else if (isAppDirectory) {
            this.selectedProductInstance = APP_DIRECTORY_OPTION;
          }
        }

        if (selectedItem) {
          this.goToItem(selectedItem, false);
        } else if (this.homeOrganizationId) {
          // emit the org if the user has no instance permissions but has an org available
          this.select.emit([{ text: this.orgName || '', type: 'organization', value: this.homeOrganizationId }]);
        }
      } catch (error) {
        console.error(error);
        this.loading = false;
      } finally {
        this.fetchingPermissions = false;
        this.loading = false;
      }
    }
  }

  @Listen('opti-dropdown-list-selection')
  onItemSelection(event: CustomEvent<DropdownItem>) {
    const item: DropdownItem = event.detail || {
      text: this.orgName || 'Optimizely Organization',
    };

    if (item?.value !== this.selectedProductInstance?.value) {
      this.goToItem(item);
    }
  }

  goToItem(item: DropdownItem, navigate: boolean = true) {
    let updatedOrg: DropdownItem = this.selectedOrganization;
    let updatedProductInstance: DropdownItem = this.selectedProductInstance;

    const { type, valueObject, subItems } = item || {};
    const noInstanceId = !this.instanceId;

    if (['instance', 'product'].includes(type)) {
      if (!subItems?.length) {
        const prevValueObject = this.selectedProductInstance?.valueObject;
        updatedProductInstance = item;
        updatedOrg = this.data.organizations.find(org => org.value === valueObject.OrganizationId) || this.selectedOrganization;

        if (this.user?.email && this.orgCrmId) {
          const payload = {
            organizationId: updatedOrg.value,
            organizationName: updatedOrg.text,
            currentProductName: prevValueObject?.ProductName || DASHBOARD_PRODUCT_ID,
            currentInstanceName: prevValueObject?.InstanceName || DASHBOARD_PRODUCT_ID,
            switchedToProductName: valueObject?.ProductName || DASHBOARD_PRODUCT_ID,
            switchedToInstanceName: valueObject?.InstanceName || DASHBOARD_PRODUCT_ID,
            url: item.href,
          };

          emitSegmentTrackEvent({
            customerId: this.orgCrmId,
            email: this.user.email,
            eventName: 'Product Switch',
            data: payload,
          });
        }
      }
    } else if (item.type === 'organization') {
      if (item?.value !== updatedOrg?.value && this.user?.email && this.orgCrmId) {
        emitSegmentTrackEvent({
          customerId: this.orgCrmId,
          email: this.user.email,
          eventName: 'Account Switch',
          data: {
            currentOrganizationId: updatedOrg.value,
            currentOrganizationName: updatedOrg.text,
            switchedToOrganizationId: item?.value,
            switchedToOrganizationName: item?.text,
          },
        });
      }

      // When switching org, users are navigated to the dashboard,
      updatedOrg = item;
      // set the dashboard / app directory option as selected if we are going to navigate there, provided instance id matches, or a fallback isn't provided
      if (this.selectedProductInstance || noInstanceId) {
        updatedProductInstance = this.selectedProductInstance?.value === APP_DIRECTORY_PRODUCT_ID ? APP_DIRECTORY_OPTION : DASHBOARD_OPTION;
      }
    }

    this.selectedOrganization = updatedOrg;
    this.selectedProductInstance = updatedProductInstance;

    if (this.selectedProductInstance) {
      this.select.emit([this.selectedOrganization, this.selectedProductInstance]);
      if (
        !Boolean(Env.DISABLE_NAVIGATION) &&
        (navigate || noInstanceId) &&
        this.selectedProductInstance.href &&
        !window.location.href.includes(this.selectedProductInstance.href)
      ) {
        const urlWithParams = new URL(this.selectedProductInstance.href.replace('<organizationId>', `${this.selectedOrganization?.value}`));
        if (this.selectedProductInstance.href !== 'https://sa2.commerce.opti-demo.online/admin') {
          urlWithParams.searchParams.append('login_hint', this.user?.email);
        }
        if (this.selectedProductInstance.value === DASHBOARD_OPTION.value) {
          urlWithParams.searchParams.append('orgId', `${this.selectedOrganization?.value}`);
        }
        if (!this.disableNavigation) {
          window.location.href = urlWithParams.toString();
        }
      }
    }
  }

  async _getHomeOrganization(email, token) {
    const userResponse = await fetch(`${Env.IDENTITY_SERVICE_URL}/api/users/${email.toLowerCase()}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!userResponse.ok) {
      throw new Error(userResponse.statusText);
    }

    const userData = await userResponse.json();

    return userData.HomeOrganizationId;
  }

  render() {
    const { organizations, products } = this.data || {};
    const isDashboard = IS_DASHBOARD({ item: this.selectedProductInstance });
    const isAppDirectory = IS_APP_DIRECTORY({ item: this.selectedProductInstance });

    const parentProduct = isDashboard || isAppDirectory ? null : products.find(product => product.valueObject.ProductId === this.selectedProductInstance?.valueObject.ProductId);

    const selectedOrg = this.selectedOrganization ? [this.selectedOrganization] : null;
    const selectedProduct = parentProduct ? [parentProduct, this.selectedProductInstance] : this.selectedProductInstance ? [this.selectedProductInstance] : null;

    const productSwitcherOptions = this.enableAppDirectory
      ? [DASHBOARD_OPTION, APP_DIRECTORY_OPTION, ...products.filter(product => product.valueObject.OrganizationId === this.selectedOrganization?.value)]
      : [DASHBOARD_OPTION, ...products.filter(product => product.valueObject.OrganizationId === this.selectedOrganization?.value)];

    return (
      <div class="product-switcher no-select">
        <opti-nav-menu class="org" loading={this.loading || this.showLoading} options={organizations} selected={selectedOrg} filterPlaceholder="Filter accounts...">
          <span slot="default-text">{this.orgName || 'Optimizely'}</span>
        </opti-nav-menu>
        <span class="divider">
          <SVGIcon name="slash-forward"></SVGIcon>
        </span>
        <opti-nav-menu
          class="product"
          loading={this.loading || this.showLoading}
          options={productSwitcherOptions}
          selected={selectedProduct}
          filterPlaceholder="Filter products & instances..."
        >
          <span slot="default-text">{this.productName}</span>
        </opti-nav-menu>
      </div>
    );
  }
}
